/**
 * The one rule for ordering players: conservative rating (mu - 3 sigma), highest first.
 *
 * mu alone rewards luck over few games, so a player with one win would outrank a player
 * with 100 solid games. Every list, recap position and CLI leaderboard uses this helper
 * (the backend equivalent is TrueSkillCalculator.get_conservative_rating).
 *
 * Win probability and team strength stay on mu: they estimate an outcome, not an order.
 */

/** How many sigmas are subtracted from mu. */
export const CONSERVATIVE_SIGMA_FACTOR = 3;

/** The rating we're confident the player has reached: mu - 3 sigma. */
export const conservativeRating = (mu: number, sigma: number): number =>
  mu - CONSERVATIVE_SIGMA_FACTOR * sigma;

/** The fields the ordering needs. */
export interface RankablePlayer {
  name: string;
  /** mu */
  rating: number;
  sigma: number;
  gamesPlayed: number;
}

/**
 * Sort comparator: players with games first, by conservative rating (highest first), then
 * more games, then name. Players with no games come last, alphabetically.
 */
export const comparePlayersByRank = (a: RankablePlayer, b: RankablePlayer): number => {
  const aPlayed = a.gamesPlayed > 0;
  const bPlayed = b.gamesPlayed > 0;
  if (aPlayed !== bPlayed) return aPlayed ? -1 : 1;
  if (aPlayed && bPlayed) {
    const diff = conservativeRating(b.rating, b.sigma) - conservativeRating(a.rating, a.sigma);
    if (diff !== 0) return diff;
    if (a.gamesPlayed !== b.gamesPlayed) return b.gamesPlayed - a.gamesPlayed;
  }
  return a.name.localeCompare(b.name);
};

/** Short explanation shown next to the number the lists sort by. */
export const CONSERVATIVE_RATING_EXPLANATION =
  "The score we're confident you've reached: your skill estimate minus three times its uncertainty. It rises as you play more games.";
